import React, { useState, useEffect, useRef } from "react";
import { FaCheck } from "react-icons/fa6";
import Section from "./section";

const steps = [
  {
    name: 'Audit & Strategy',
    description: 'Identify your biggest revenue gaps and prioritize quick-win opportunities',
    href: '#',
  },
  {
    name: 'Design, Build & Automate',
    description: 'Craft on-brand templates and set up targeted workflows',
    href: '#',
  },
  {
    name: 'Launch & Segment',
    description: 'Deploy campaigns to the right audience at exactly the right moment',
    href: '#',
  },
  {
    name: 'Analyze & Iterate',
    description: 'Review performance data and refine for ongoing uplift',
    href: '#',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function HowItWorks() {
  const containerRef = useRef(null);
  const stepRefs = useRef([]);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(Array(steps.length).fill(false));
  const [barBounds, setBarBounds] = useState({ top: 0, bottom: 0 });
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    function updateBarBounds() {
      if (!containerRef.current || stepRefs.current.length === 0) return;
      const ch = containerRef.current.clientHeight;
      const firstEl = stepRefs.current[0];
      const lastEl = stepRefs.current[steps.length - 1];
      if (!firstEl || !lastEl) return;
      const firstCenter = firstEl.offsetTop + firstEl.offsetHeight / 2;
      const lastCenter = lastEl.offsetTop + lastEl.offsetHeight / 2;
      setBarBounds({ top: firstCenter, bottom: ch - lastCenter });
      setContainerHeight(ch);
    }
    updateBarBounds();
    window.addEventListener("resize", updateBarBounds);
    return () => window.removeEventListener("resize", updateBarBounds);
  }, []);

  useEffect(() => {
    function onScroll() {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const ch = containerRef.current.clientHeight;
      const effectiveBarHeight = ch - barBounds.top - barBounds.bottom;
      const viewportMiddle = window.innerHeight / 1.75;
      const progressValue =
        (viewportMiddle - (containerRect.top + barBounds.top)) /
        effectiveBarHeight;
      const newProgress = Math.min(Math.max(progressValue, 0), 1);
      setProgress(newProgress);

      const newCompleted = steps.map((_, index) => {
        const stepEl = stepRefs.current[index];
        if (!stepEl) return false;
        const stepCenterRelative =
          stepEl.offsetTop + stepEl.offsetHeight / 2 - barBounds.top;
        return newProgress * effectiveBarHeight >= stepCenterRelative;
      });
      setCompleted(newCompleted);
    }

    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [barBounds, containerHeight]);

  return (
    <Section boldText="Our 4-Step Process">
      <div ref={containerRef} className="relative mx-auto max-w-6xl hue-rotate-[30deg]">
        <div
          className="absolute left-4 w-0.5 bg-gray-300"
          style={{ top: barBounds.top, bottom: barBounds.bottom }}
        >
          <div
            style={{
              height: `${progress * (containerHeight - barBounds.top - barBounds.bottom)}px`,
            }}
            className="w-full bg-vblue3"
          />
        </div>

        <nav aria-label="Progress">
          <ol role="list" className="relative pt-10">
            {steps.map((step, index) => (
              <li
                key={step.name}
                ref={(el) => (stepRefs.current[index] = el)}
                className="relative mb-16"
              >
                <div className="flex items-center">
                  <div className="flex h-9 items-center">
                    <div
                      className={classNames(
                        "relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2",
                        completed[index]
                          ? "animate-pop border-vblue3 bg-vblue3"
                          : "border-gray-300 bg-white",
                      )}
                    >
                      {completed[index] && (
                        <FaCheck
                          aria-hidden="true"
                          className="h-5 w-5 text-white"
                        />
                      )}
                    </div>
                  </div>
                  <div className="ml-4 min-w-0 text-lg">
                    <span
                      className={classNames(
                        "font-medium",
                        completed[index] ? "text-vblue3" : "text-gray-500",
                      )}
                    >
                      {step.name} –
                    </span>{" "}
                    <span
                      className={classNames(
                        "font-medium",
                        completed[index] ? "text-black" : "text-gray-500",
                      )}
                    >
                      {step.description}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </nav>

        {/* CSS for the checkmark "pop" animation */}
        <style jsx>{`
          @keyframes pop {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.3);
              opacity: 1;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          .animate-pop {
            animation: pop 0.5s ease-out forwards;
          }
        `}</style>
      </div>
    </Section>
  );
}
